import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import AdvancedAnalysis from './components/AdvancedAnalysis';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './HomePage';
import ContactUsPage from './ContactUsPage';
import SupportTicketsPage from './SupportTicketsPage';
import SupportTicketNewPage from './SupportTicketNewPage';
import SupportTicketDetailPage from './SupportTicketDetailPage';
import UploadPage from './UploadPage';
import JobsListPage from './JobsListPage';
import JobDetailsPage from './JobDetailsPage';
import CandidateListPage from './CandidateListPage';
import InterviewsPage from './InterviewsPage';
import ScheduleInterviewPage from './ScheduleInterviewPage';
import LoginPage from './components/LoginPage';
import ForgotPasswordPage from './ForgotPasswordPage';
import ResetPasswordPage from './ResetPasswordPage';
import AuthCallback from './components/AuthCallback';
import ProtectedRoute from './components/ProtectedRoute';
import GreetingPage from './GreetingPage';
import ApplyPage from './ApplyPage';
import ThankYouPage from './ThankYouPage';
import AlreadyAppliedPage from './AlreadyAppliedPage';
import ApplicationTrackingPage from './ApplicationTrackingPage';
import CreateJobPostingPage from './CreateJobPostingPage';
import ApplicationFormSetupPage from './ApplicationFormSetupPage';
import EvaluationDashboard from './components/EvaluationDashboard';
import EvaluationResults from './components/EvaluationResults';
import MetricCharts from './components/MetricCharts';
import RunEvaluation from './components/RunEvaluation';
import LLMSettingsPage from './components/LLMSettingsPage';
import SettingsPage from './components/SettingsPage';
import MonitoringDashboard from './components/MonitoringDashboard';
import {
  PlatformConsolePage,
  PendingMembershipPage,
  AcceptInvitePage,
  AccountInactivePage,
} from './components/GovernancePages';
import './App.css';

/** Keep browser tab title as TalentVibe on every route (and in-app navigation). */
function DocumentTitleSync() {
  const location = useLocation();
  useEffect(() => {
    document.title = 'TalentVibe';
  }, [location.pathname]);
  return null;
}

// Component to conditionally render app layout
function AppLayout({ children }) {
  const location = useLocation();
  const isApplicationRoute = location.pathname.startsWith('/apply/');
  
  if (isApplicationRoute) {
    // Standalone application form - no header/footer
    return <>{children}</>;
  }
  
  // Regular app routes - with header/footer
  return (
    <>
      <Header />
      <main className="app-content">
        {children}
      </main>
      <Footer />
    </>
  );
}

function App() {
  return (
    <Router>
      <div className="App">
        <DocumentTitleSync />
        <AppLayout>
          <Routes>
            {/* Login route */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            
            {/* OAuth/SSO callback — receives JWT tokens from backend redirect */}
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/platform" element={<ProtectedRoute><PlatformConsolePage /></ProtectedRoute>} />
            <Route path="/pending-membership" element={<ProtectedRoute><PendingMembershipPage /></ProtectedRoute>} />
            <Route path="/account-inactive" element={<ProtectedRoute><AccountInactivePage /></ProtectedRoute>} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            
            {/* Public application routes - STANDALONE, no header/footer */}
            <Route path="/apply/t/:slug" element={<ApplyPage />} />
            <Route path="/apply/t/:slug/thank-you" element={<ThankYouPage />} />
            <Route path="/apply/t/:slug/already-applied" element={<AlreadyAppliedPage />} />
            <Route path="/apply/:jobId/greeting" element={<GreetingPage />} />
            <Route path="/apply/:jobId" element={<ApplyPage />} />
            <Route path="/apply/:jobId/thank-you" element={<ThankYouPage />} />
            <Route path="/apply/:jobId/already-applied" element={<AlreadyAppliedPage />} />
            <Route path="/apply/track/:token" element={<ApplicationTrackingPage />} />
            
            {/* Protected routes - require authentication */}
            <Route path="/upload" element={<ProtectedRoute><UploadPage /></ProtectedRoute>} />
            <Route path="/jobs/create" element={<ProtectedRoute><CreateJobPostingPage /></ProtectedRoute>} />
            <Route path="/jobs/:jobId/create-posting" element={<ProtectedRoute><CreateJobPostingPage /></ProtectedRoute>} />
            <Route path="/jobs" element={<ProtectedRoute><JobsListPage /></ProtectedRoute>} />
            <Route path="/jobs/:jobId" element={<ProtectedRoute><JobDetailsPage /></ProtectedRoute>} />
            <Route path="/jobs/:jobId/setup-form" element={<ProtectedRoute><ApplicationFormSetupPage /></ProtectedRoute>} />
            <Route path="/jobs/:jobId/candidates" element={<ProtectedRoute><CandidateListPage /></ProtectedRoute>} />
            <Route path="/interviews" element={<ProtectedRoute><InterviewsPage /></ProtectedRoute>} />
            <Route path="/schedule-interview/:jobId/:resumeId" element={<ProtectedRoute><ScheduleInterviewPage /></ProtectedRoute>} />
            {/* Public: candidate opens link from email (token-only URL; no login required) */}
            <Route path="/select-slots/t/:token" element={<ScheduleInterviewPage />} />
            <Route path="/select-slots/:jobId/:resumeId" element={<ScheduleInterviewPage />} />
            <Route path="/evals" element={<ProtectedRoute><EvaluationDashboard /></ProtectedRoute>} />
            <Route path="/evals/run" element={<ProtectedRoute><RunEvaluation /></ProtectedRoute>} />
            <Route path="/evals/results" element={<ProtectedRoute><EvaluationResults /></ProtectedRoute>} />
            <Route path="/evals/results/:runId" element={<ProtectedRoute><EvaluationResults /></ProtectedRoute>} />
            <Route path="/evals/charts" element={<ProtectedRoute><MetricCharts /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/settings/llm" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/settings/sso" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/settings/email" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/monitoring" element={<ProtectedRoute><MonitoringDashboard /></ProtectedRoute>} />
            <Route path="/contact" element={<ContactUsPage />} />
            <Route path="/support" element={<ProtectedRoute><SupportTicketsPage /></ProtectedRoute>} />
            <Route path="/support/new" element={<ProtectedRoute><SupportTicketNewPage /></ProtectedRoute>} />
            <Route path="/support/:ticketId" element={<ProtectedRoute><SupportTicketDetailPage /></ProtectedRoute>} />
            <Route path="/" element={<HomePage />} />
          </Routes>
        </AppLayout>
        {/* Force AdvancedAnalysis to be included in build */}
        <div style={{display: 'none'}}>
          <AdvancedAnalysis advancedAnalysis={{}} />
        </div>
      </div>
    </Router>
  );
}

export default App;
